import { useRef, useState, useCallback, useEffect } from "react";

// Interactive comparison of the supplied before and after photographs.
export function BeforeAfterSlider({ before, after, beforeLabel = "Blueprint", afterLabel = "Built", testId = "before-after" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[16/10] overflow-hidden select-none border border-border"
      data-testid={testId}
    >
      {/* After (full) */}
      <img src={after} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={before}
          alt={beforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Labels */}
      <span className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-widest uppercase bg-background/80 text-foreground px-2 py-1 border border-border">
        {beforeLabel}
      </span>
      <span className="absolute top-3 right-3 font-mono-tech text-[10px] tracking-widest uppercase bg-primary text-primary-foreground px-2 py-1">
        {afterLabel}
      </span>

      {/* Handle */}
      <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[2px] h-full bg-primary" />
        <button
          aria-label="Drag to compare"
          data-testid={`${testId}-handle`}
          onMouseDown={() => (dragging.current = true)}
          onTouchStart={() => (dragging.current = true)}
          onKeyDown={(event) => {
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
              event.preventDefault();
              setPos(current => event.key === 'Home' ? 0 : event.key === 'End' ? 100 : Math.max(0, Math.min(100, current + (event.key === 'ArrowRight' ? 5 : -5))));
            }
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-primary text-primary-foreground cursor-ew-resize"
        >
          <span className="font-mono-tech text-xs">⇆</span>
        </button>
      </div>

      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Before and after comparison"
        className="sr-only"
        data-testid={`${testId}-range`}
      />
    </div>
  );
}
